import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@/icons';
import { Modal, ELEVATED_MODAL_Z_INDEX } from './Modal';
import { useTheme } from '../contexts/ThemeContext';

interface ProTrialDowngradeConfirmModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
    isCountLoading: boolean;
    totalCases: number | null;
    casesWithProData: number | null;
    /** Cases whose Pro-data verdict isn't computed yet -- unknown, not confirmed-safe. */
    unknownCases: number | null;
    /** The count fetch itself failed (network/500). Distinct from "still loading." */
    countLoadFailed: boolean;
    onRetryCountLoad: () => void;
    /**
     * Whether the case read-only lock is actually in force (the ProDowngrade flag). With it off,
     * no case locks, so quoting numbers would be a lie.
     */
    showCaseLockWarning: boolean;
}

/**
 * ProTrial: confirms moving the whole team from Pro to Standard after a lapsed trial.
 *
 * Tells the user exactly how many of their cases will become read-only BEFORE they commit. Only
 * cases that genuinely use a Pro feature lock, so for most teams this number is small or zero --
 * which is the point of showing it rather than a vague warning.
 */
export const ProTrialDowngradeConfirmModal: React.FC<ProTrialDowngradeConfirmModalProps> = ({
    isOpen,
    onCancel,
    onConfirm,
    isSubmitting,
    isCountLoading,
    totalCases,
    casesWithProData,
    unknownCases,
    countLoadFailed,
    onRetryCountLoad,
    showCaseLockWarning,
}) => {
    const theme = useTheme();

    const knowCounts = totalCases !== null && casesWithProData !== null;
    const anyWillLock = knowCounts && casesWithProData! > 0;
    const anyUnknown = knowCounts && (unknownCases ?? 0) > 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            title="Downgrade to Standard?"
            // Reached from the mandatory ProTrialEndedModal -- keep the same elevated priority
            // through this step rather than sinking back below an ordinary case-level modal.
            zIndex={ELEVATED_MODAL_Z_INDEX}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <p style={{ margin: 0, color: theme.colors.text.primary }}>
                    This moves your whole team to Standard. Everyone will be signed out and will
                    need to log in again.
                </p>

                {showCaseLockWarning && isCountLoading && (
                    <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                        Checking which of your cases use Pro features…
                    </p>
                )}

                {showCaseLockWarning && !isCountLoading && countLoadFailed && (
                    <div style={{
                        display: 'flex',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.error}`,
                        backgroundColor: theme.colors.status.error + '15',
                    }}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: theme.colors.status.error, marginTop: '2px' }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs, alignItems: 'flex-start' }}>
                            <strong style={{ color: theme.colors.text.primary }}>
                                We could not check which of your cases will become read-only.
                            </strong>
                            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                Try again before downgrading, so you know what this affects.
                            </span>
                            <button
                                type="button"
                                onClick={onRetryCountLoad}
                                style={{
                                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                    borderRadius: theme.borderRadius.sm,
                                    border: `1px solid ${theme.colors.status.error}`,
                                    backgroundColor: 'transparent',
                                    color: theme.colors.status.error,
                                    cursor: 'pointer',
                                    fontSize: theme.typography.fontSize.sm,
                                }}
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                )}

                {showCaseLockWarning && !isCountLoading && !countLoadFailed && anyWillLock && (
                    <div style={{
                        display: 'flex',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.warning}`,
                        backgroundColor: theme.colors.status.warning + '15',
                    }}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: theme.colors.status.warning, marginTop: '2px' }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                            <strong style={{ color: theme.colors.text.primary }}>
                                {casesWithProData} of your {totalCases} cases use Pro features and will become read-only.
                            </strong>
                            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                You can still open, copy and download them. Copying removes the
                                Pro-only data so the copy is editable again.
                            </span>
                            {anyUnknown && (
                                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    {unknownCases} more {unknownCases === 1 ? 'case has' : 'cases have'} not been checked
                                    yet and may also be affected.
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {showCaseLockWarning && !isCountLoading && !countLoadFailed && knowCounts && !anyWillLock && !anyUnknown && (
                    <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                        None of your {totalCases} cases use Pro features, so nothing will become read-only.
                    </p>
                )}

                {showCaseLockWarning && !isCountLoading && !countLoadFailed && knowCounts && !anyWillLock && anyUnknown && (
                    <div style={{
                        display: 'flex',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.warning}`,
                        backgroundColor: theme.colors.status.warning + '15',
                    }}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: theme.colors.status.warning, marginTop: '2px' }}
                        />
                        <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.primary }}>
                            None of your {totalCases} checked cases use Pro features, but {unknownCases}{' '}
                            {unknownCases === 1 ? 'case has' : 'cases have'} not been checked yet and may
                            also become read-only.
                        </span>
                    </div>
                )}

                {!showCaseLockWarning && (
                    <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                        Your team will lose access to Pro features.
                    </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.border.default}`,
                            backgroundColor: theme.colors.surface,
                            color: theme.colors.text.primary,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            opacity: isSubmitting ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        // Don't let them commit before we can tell them what it costs -- including
                        // when the count fetch failed outright, not just while it's in flight.
                        disabled={isSubmitting || (showCaseLockWarning && (isCountLoading || countLoadFailed))}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.status.error}`,
                            backgroundColor: theme.colors.status.error,
                            color: theme.colors.text.inverse,
                            cursor: (isSubmitting || (showCaseLockWarning && (isCountLoading || countLoadFailed))) ? 'not-allowed' : 'pointer',
                            opacity: (isSubmitting || (showCaseLockWarning && (isCountLoading || countLoadFailed))) ? 0.6 : 1,
                        }}
                    >
                        {isSubmitting ? 'Downgrading…' : 'Downgrade to Standard'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
